import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  raxApi,
  type AutonomyLevel,
  type AutonomyPolicyMode,
  type AutonomyPreferences,
} from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";
import {
  CORE_SKILLS,
  getSkillsByCategory,
  CATEGORY_INFO,
  type SkillDefinition,
  type SkillCategory,
} from "../../../../src/rax/skills-registry.js";

const LEVEL_OPTIONS: Array<{ value: AutonomyLevel; label: string; description: string }> = [
  {
    value: "copilot",
    label: "Copilot",
    description: "Assistive mode. Prefer explicit confirmation before outward actions.",
  },
  {
    value: "balanced",
    label: "Balanced",
    description: "Default mode. Autonomous checks, guarded outward actions.",
  },
  {
    value: "self_supply",
    label: "Self-Supply",
    description: "High autonomy. Build and execute with minimal prompting while respecting policy.",
  },
];

const POLICY_OPTIONS: Array<{ value: AutonomyPolicyMode; label: string; description: string }> = [
  {
    value: "ask_first",
    label: "Ask First",
    description: "Require explicit approval before execution.",
  },
  {
    value: "on_request",
    label: "On Request",
    description: "Execute when directly requested in the turn.",
  },
  {
    value: "always_check",
    label: "Always Check",
    description: "Run automatically for information gathering.",
  },
  {
    value: "proactive_notify",
    label: "Proactive Notify",
    description: "Run checks and proactively notify.",
  },
];

// Dynamic skills from registry - grouped by category
function getSkillsForAutonomy(): Array<{ key: string; label: string; category: string; description?: string; icon?: string }> {
  const tasks: Array<{ key: string; label: string; category: string; description?: string; icon?: string }> = [];
  for (const skill of CORE_SKILLS) {
    for (const action of skill.actions) {
      tasks.push({
        key: action.key,
        label: action.label,
        category: skill.name,
        description: action.description,
        icon: skill.icon,
      });
    }
  }
  return tasks;
}

@customElement("rax-autonomy-settings")
export class RaxAutonomySettings extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
        padding: var(--space-6);
      }
      
      .page {
        display: grid;
        gap: var(--space-5);
      }
      
      .card {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        padding: var(--space-5);
      }
      
      .title {
        font-size: var(--font-xl);
        font-weight: 700;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }
      
      .subtitle {
        color: var(--text-secondary);
        font-size: var(--font-sm);
        line-height: 1.45;
      }
      
      .grid {
        margin-top: var(--space-4);
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: var(--space-4);
      }
      
      .field {
        display: grid;
        gap: var(--space-2);
      }
      
      .label {
        font-size: var(--font-xs);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--text-tertiary);
      }
      
      select {
        width: 100%;
        border: 1px solid var(--border-color);
        background: var(--bg-elevated);
        color: var(--text-primary);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-size: var(--font-sm);
      }
      
      select:focus {
        outline: none;
        border-color: var(--rax-yellow);
      }
      
      .hint {
        color: var(--text-tertiary);
        font-size: var(--font-xs);
        line-height: 1.35;
      }
      
      .task-row {
        display: grid;
        grid-template-columns: 1fr minmax(220px, 260px);
        gap: var(--space-3);
        align-items: center;
        border-bottom: 1px solid var(--border-color);
        padding: var(--space-3) 0;
      }
      
      .task-row:last-child {
        border-bottom: none;
      }
      
      .task-label {
        color: var(--text-primary);
        font-size: var(--font-sm);
        font-weight: 600;
      }
      
      .task-category {
        color: var(--text-tertiary);
        font-size: var(--font-xs);
      }

      .task-description {
        color: var(--text-tertiary);
        font-size: var(--font-xs);
        margin-top: 2px;
        line-height: 1.3;
      }

      .category-section {
        margin-bottom: var(--space-4);
      }

      .category-header {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) 0;
        font-weight: 600;
        color: var(--text-secondary);
        font-size: var(--font-sm);
        border-bottom: 1px solid var(--border-color);
        margin-bottom: var(--space-2);
      }

      .category-icon {
        font-size: 14px;
      }

      .skill-icon {
        margin-right: 4px;
        font-size: 12px;
      }

      .actions {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: var(--space-3);
        margin-top: var(--space-4);
      }
      
      .status {
        color: var(--text-tertiary);
        font-size: var(--font-xs);
      }
      
      .status.error {
        color: var(--error);
      }
      
      .save-btn {
        border: none;
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-radius: var(--radius-md);
        padding: var(--space-2) var(--space-4);
        font-weight: 700;
        cursor: pointer;
      }
      
      .save-btn[disabled] {
        opacity: 0.6;
        cursor: wait;
      }
      
      @media (max-width: 900px) {
        :host {
          padding: var(--space-4);
        }
      
        .card {
          padding: var(--space-4);
        }
      
        .task-row {
          grid-template-columns: minmax(0, 1fr);
          gap: var(--space-2);
        }
      }
      
      @media (max-width: 640px) {
        :host {
          padding: var(--space-3);
        }
      
        .title {
          font-size: var(--font-lg);
        }
      
        .actions {
          flex-direction: column;
          align-items: stretch;
        }
      
        .save-btn {
          width: 100%;
        }
      }
    `,
  ];

  @state() private loading = true;
  @state() private saving = false;
  @state() private error: string | null = null;
  @state() private savedAt: string | null = null;
  @state() private prefs: AutonomyPreferences | null = null;
  @state() private draft: AutonomyPreferences | null = null;

  override async connectedCallback() {
    super.connectedCallback();
    await this.load();
  }

  private async load() {
    this.loading = true;
    this.error = null;
    try {
      const res = await raxApi.getAutonomyPreferences();
      if (!res.success || !res.data) {
        throw new Error(res.error || "Failed to load autonomy preferences");
      }
      this.prefs = {
        ...res.data,
        actionDefaults: { ...res.data.actionDefaults },
        taskPolicies: { ...(res.data.taskPolicies || {}) },
      };
      this.draft = {
        ...res.data,
        actionDefaults: { ...res.data.actionDefaults },
        taskPolicies: { ...(res.data.taskPolicies || {}) },
      };
      this.savedAt = res.data.updatedAt || null;
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load autonomy settings";
    } finally {
      this.loading = false;
    }
  }

  private onLevelChange(e: Event) {
    if (!this.draft) return;
    const value = (e.target as HTMLSelectElement).value as AutonomyLevel;
    this.draft = { ...this.draft, level: value };
  }

  private onDefaultChange(kind: "check" | "do", e: Event) {
    if (!this.draft) return;
    const value = (e.target as HTMLSelectElement).value as AutonomyPolicyMode;
    this.draft = {
      ...this.draft,
      actionDefaults: {
        ...this.draft.actionDefaults,
        [kind]: value,
      },
    };
  }

  private onTaskPolicyChange(taskKey: string, e: Event) {
    if (!this.draft) return;
    const value = (e.target as HTMLSelectElement).value as AutonomyPolicyMode | "";
    const nextPolicies = { ...(this.draft.taskPolicies || {}) };
    if (!value) {
      delete nextPolicies[taskKey];
    } else {
      nextPolicies[taskKey] = value;
    }
    this.draft = { ...this.draft, taskPolicies: nextPolicies };
  }

  private async save() {
    if (!this.draft || this.saving) return;
    this.saving = true;
    this.error = null;
    try {
      const res = await raxApi.updateAutonomyPreferences({
        level: this.draft.level,
        actionDefaults: this.draft.actionDefaults,
        taskPolicies: this.draft.taskPolicies,
      });
      if (!res.success || !res.data) {
        throw new Error(res.error || "Failed to save autonomy preferences");
      }
      this.prefs = {
        ...res.data,
        actionDefaults: { ...res.data.actionDefaults },
        taskPolicies: { ...(res.data.taskPolicies || {}) },
      };
      this.draft = {
        ...res.data,
        actionDefaults: { ...res.data.actionDefaults },
        taskPolicies: { ...(res.data.taskPolicies || {}) },
      };
      this.savedAt = res.data.updatedAt || new Date().toISOString();
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to save autonomy settings";
    } finally {
      this.saving = false;
    }
  }

  private hasChanges(): boolean {
    if (!this.prefs || !this.draft) return false;
    return JSON.stringify(this.prefs) !== JSON.stringify(this.draft);
  }

  private formatSavedAt(): string {
    if (!this.savedAt) return "Not saved yet";
    const dt = new Date(this.savedAt);
    return Number.isNaN(dt.getTime()) ? "Saved" : `Saved ${dt.toLocaleString()}`;
  }

  private renderSkillsByCategory() {
    const skillsByCategory = getSkillsByCategory();
    const categories = Object.entries(skillsByCategory).filter(([, skills]) => skills.length > 0);

    return html`
      ${categories.map(([category, skills]) => {
        const info = CATEGORY_INFO[category as SkillCategory];
        return html`
          <div class="category-section">
            <div class="category-header">
              <span class="category-icon">${info?.icon || "📦"}</span>
              <span class="category-name">${info?.label || category}</span>
            </div>
            ${skills.map((skill: SkillDefinition) =>
              skill.actions.map((action) => html`
                <div class="task-row">
                  <div>
                    <div class="task-label">
                      <span class="skill-icon">${skill.icon}</span>
                      ${action.label}
                    </div>
                    <div class="task-category">${skill.name} · <code>${action.key}</code></div>
                    ${action.description ? html`<div class="task-description">${action.description}</div>` : ""}
                  </div>
                  <select
                    .value=${this.draft?.taskPolicies?.[action.key] || ""}
                    @change=${(e: Event) => this.onTaskPolicyChange(action.key, e)}
                  >
                    <option value="">Inherit default</option>
                    ${POLICY_OPTIONS.map(
                      (opt) => html`<option value=${opt.value}>${opt.label}</option>`,
                    )}
                  </select>
                </div>
              `)
            )}
          </div>
        `;
      })}
    `;
  }

  override render() {
    if (this.loading) {
      return html`
        <div class="card">Loading autonomy policy…</div>
      `;
    }
    if (!this.draft) {
      return html`
        <div class="card">No autonomy policy loaded.</div>
      `;
    }

    const levelDescription =
      LEVEL_OPTIONS.find((opt) => opt.value === this.draft?.level)?.description || "";

    return html`
      <div class="page">
        <section class="card">
          <div class="title">Policy Controls</div>
          <div class="subtitle">
            Define how Rax handles <strong>checks</strong> (information gathering) vs
            <strong>actions</strong> (outward changes) across tools and integrations.
          </div>
          <div class="grid">
            <div class="field">
              <div class="label">Autonomy Level</div>
              <select .value=${this.draft.level} @change=${this.onLevelChange}>
                ${LEVEL_OPTIONS.map(
                  (opt) => html`<option value=${opt.value}>${opt.label}</option>`,
                )}
              </select>
              <div class="hint">${levelDescription}</div>
            </div>
            <div class="field">
              <div class="label">Default Check Policy</div>
              <select
                .value=${this.draft.actionDefaults.check}
                @change=${(e: Event) => this.onDefaultChange("check", e)}
              >
                ${POLICY_OPTIONS.map(
                  (opt) => html`<option value=${opt.value}>${opt.label}</option>`,
                )}
              </select>
              <div class="hint">Applied when no task-specific override is set.</div>
            </div>
            <div class="field">
              <div class="label">Default Action Policy</div>
              <select
                .value=${this.draft.actionDefaults.do}
                @change=${(e: Event) => this.onDefaultChange("do", e)}
              >
                ${POLICY_OPTIONS.map(
                  (opt) => html`<option value=${opt.value}>${opt.label}</option>`,
                )}
              </select>
              <div class="hint">Use <code>Ask First</code> for the safest default.</div>
            </div>
          </div>
        </section>

        <section class="card">
          <div class="title">Task Overrides</div>
          <div class="subtitle">
            Set explicit policies for high-value tasks. Leave empty to inherit defaults.
          </div>
          ${this.renderSkillsByCategory()}

          <div class="actions">
            <div class="status ${this.error ? "error" : ""}">
              ${this.error ? this.error : this.formatSavedAt()}
            </div>
            <button class="save-btn" ?disabled=${!this.hasChanges() || this.saving} @click=${this.save}>
              ${this.saving ? "Saving…" : "Save Policy"}
            </button>
          </div>
        </section>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-autonomy-settings": RaxAutonomySettings;
  }
}
